// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

// Admin API client for the GLOBAL + per-brokerage projection-assumption overrides
// that drive the relationship cashflow / Future-Values projections. Platform admin
// edits the global default and any brokerage's override; advisors edit their own
// advisor/brokerage/relationship levels in the Advisor UI. The admin-bff proxies
// /api/** to the resource-server. See docs/architecture/layered-projection-assumptions.md.

export interface ProjectionAssumptions {
  priceInflationRate: number | null;
  earningsGrowthRate: number | null;
  statePensionAnnual: number | null;
  pensionDrawdownRate: number | null;
  expenditureAtRetirementFactor: number | null;
  defaultSavingsGrowthRate: number | null;
  defaultInvestmentGrowthRate: number | null;
  defaultPropertyGrowthRate: number | null;
  defaultCollectableGrowthRate: number | null;
  defaultSurplusSweepRate: number | null;
  childBenefitMonthly: number | null;
  horizonYears: number | null;
}

export const ASSUMPTION_FIELDS: Array<{
  key: keyof ProjectionAssumptions;
  label: string;
  suffix: string;
}> = [
  { key: 'priceInflationRate', label: 'Price inflation', suffix: '%' },
  { key: 'earningsGrowthRate', label: 'Earnings growth', suffix: '%' },
  { key: 'statePensionAnnual', label: 'State pension (annual)', suffix: '€' },
  { key: 'pensionDrawdownRate', label: 'Pension drawdown', suffix: '%' },
  { key: 'expenditureAtRetirementFactor', label: 'Expenditure at retirement', suffix: '×' },
  { key: 'defaultSavingsGrowthRate', label: 'Default savings growth', suffix: '%' },
  { key: 'defaultInvestmentGrowthRate', label: 'Default investment growth', suffix: '%' },
  { key: 'defaultPropertyGrowthRate', label: 'Default property growth', suffix: '%' },
  { key: 'defaultCollectableGrowthRate', label: 'Default collectable growth', suffix: '%' },
  { key: 'defaultSurplusSweepRate', label: 'Surplus sweep rate', suffix: '%' },
  { key: 'childBenefitMonthly', label: 'Child Benefit (per child, monthly)', suffix: '€' },
  { key: 'horizonYears', label: 'Projection horizon', suffix: 'yrs' },
];

export type AssumptionSource =
  | 'RELATIONSHIP' | 'ADVISOR' | 'BROKERAGE' | 'GLOBAL' | 'CONFIG' | 'UNSET';

/** Resolved values + per-key provenance (what a level inherits when unset). */
export interface EffectiveAssumptions extends ProjectionAssumptions {
  sources: Record<string, AssumptionSource>;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`HTTP ${response.status}: ${text || response.statusText}`);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

const GLOBAL = '/api/admin/projection-assumptions';
const brokerage = (id: string) => `${GLOBAL}/brokerages/${id}`;

export const projectionAssumptionsApi = {
  getGlobal: (): Promise<ProjectionAssumptions> => request(GLOBAL),
  saveGlobal: (body: ProjectionAssumptions): Promise<ProjectionAssumptions> =>
    request(GLOBAL, { method: 'PUT', body: JSON.stringify(body) }),
  clearGlobal: (): Promise<void> => request(GLOBAL, { method: 'DELETE' }),
  /** What the global level inherits when unset (config backstop only). */
  getGlobalInherited: (): Promise<EffectiveAssumptions> => request(`${GLOBAL}/inherited`),

  getBrokerage: (id: string): Promise<ProjectionAssumptions> => request(brokerage(id)),
  /** What a brokerage inherits when unset (Global + config). */
  getBrokerageInherited: (id: string): Promise<EffectiveAssumptions> => request(`${brokerage(id)}/inherited`),
  saveBrokerage: (id: string, body: ProjectionAssumptions): Promise<ProjectionAssumptions> =>
    request(brokerage(id), { method: 'PUT', body: JSON.stringify(body) }),
  clearBrokerage: (id: string): Promise<void> => request(brokerage(id), { method: 'DELETE' }),
};
